import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_PROJECT_DAILY_STATS } from '../../../query/project/stats/keys';
import { CumulativeProjectDailyStats } from '../../../../../pages/projects/preview/models/CumulativeProjectDailyStats';
import { ProjectDailyStatsResponse } from '../../../../models/projects/stats/ProjectDailyStatsResponse';
import { MonitorPeriod } from '../../../../types/monitor/MonitorPeriod';
import { ChatType } from '../../../../types/monitor/ChatType';
import { format } from 'date-fns';
import { StatsKeywordsData } from '../../../../../pages/projects/preview/models/StatsKeywordsData';
import { MonitorPrompt } from '../../../../models/monitor/MonitorPrompt';
import { getProjectCumulativeDailyStats } from '../../../services/project/stats/ProjectDailyStatsService';

export const useGetProjectDailyStats = (
  projectId: number | null,
  monitorPrompts: MonitorPrompt[] | null,
  period: MonitorPeriod | null,
  chatType: ChatType | null,
  from: Date | null,
  to: Date | null,
  filterChatTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const queryEnabled =
    !!projectId &&
    !!monitorPrompts &&
    monitorPrompts.length > 0 &&
    !!period &&
    !!chatType &&
    !!from &&
    !!to;

  const keywords: StatsKeywordsData[] =
    monitorPrompts?.map((prompt) => ({
      [prompt.keyword]: prompt.id,
    })) || [];

  const cumulativeProjectDailyStats: CumulativeProjectDailyStats = queryEnabled
    ? {
        projectId: projectId!,
        keywords,
        period: period!,
        chatType: chatType!,
        startDate: format(from!, 'yyyy-MM-dd'),
        endDate: format(to!, 'yyyy-MM-dd'),
        ...(filterChatTypes && filterChatTypes.length > 0 ? { filterChatTypes } : {}),
      }
    : ({} as CumulativeProjectDailyStats);

  const {
    data: projectDailyStats,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ProjectDailyStatsResponse>({
    queryKey: [GET_PROJECT_DAILY_STATS, projectId, monitorPrompts, period, chatType, from, to, filterChatTypes],
    queryFn: () => getProjectCumulativeDailyStats(cumulativeProjectDailyStats),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    projectDailyStats: queryEnabled ? projectDailyStats ?? {} : {},
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_PROJECT_DAILY_STATS, projectId, monitorPrompts, period, chatType, from, to, filterChatTypes],
        refetchType: 'active',
      }),
  };
};
